<template>
    <app-layout
        :hasChanges="form.isDirty"
    >
        <form @submit.prevent="submit">
            <page-header>
                <template #title>Edit Order</template>

                <template #actions>
                        <jet-button v-show="form.isDirty" type="submit" class="leading-none" :disabled="form.processing || !this.canPlaceOrder" >
                            Save
                        </jet-button>

                        <jet-tertiary-button
                            v-if="form.isDirty"
                            :disabled="form.processing"
                            class="leading-none"
                            @click="showUnsavedChangesModal = true"
                        >
                            Cancel
                        </jet-tertiary-button>

                        <jet-tertiary-button
                            v-else
                            :disabled="form.processing"
                            :href="route('customer_orders.index')"
                            class="leading-none"
                        >
                            Close
                        </jet-tertiary-button>
                    </template>
            </page-header>

            <div class="px-8">
                <div class="flex gap-x-12">
                    <div class="w-1/3">
                        <div class="flex items-center mb-2">
                            <jet-label for="order_date" value="Order #:" class="w-32 mb-0 leading-7" />
                            <p>{{ order.id }}</p>
                        </div>

                        <div class="flex items-center mb-2">
                            <jet-label for="order_date" value="Order Date:" class="w-32 mb-0 leading-7" />
                            <p>{{ order.order_date }}</p>
                        </div>

                        <div class="flex items-center mb-2">
                            <jet-label for="shipping_method" value="Shipping Method:" class="w-32 mb-0 leading-7 whitespace-nowrap" />
                            <p class="capitalize">{{ order.shipping_method }}</p>
                        </div>

                        <div class="flex items-center">
                            <jet-label for="ship_date" value="Ship Date:" class="w-32 mb-0 leading-7 self-start" />
                            <div class="flex flex-1 flex-col">
                                <datepicker inputClassName="dp-custom-input"
                                    v-model="form.ship_date"
                                    weekStart="0"
                                    monthNameFormat="long"
                                    :enableTimePicker="false"
                                    :autoApply="true"
                                    :is24="false"
                                    :clearable="false"
                                    :minDate="minDate"
                                    :maxDate="maxDate"
                                    :disabled-dates="customerDisabledDates"
                                    :format="formatDate"
                                />
                                <jet-input-error :message="$page.props.errors.ship_date" class="m-1" />
                            </div>
                        </div>


                    </div>

                    <div class="w-1/3">
                        <div class="flex flex-1 flex-col gap-y-2">
                            <div class="flex items-center">
                                <jet-label for="order_date" value="Customer:" class="w-32 xl:w-28 mb-0 leading-7" />
                                <p>{{ activeCustomer.name }}</p>
                            </div>

                            <div class="flex flex-1 items-start">
                                <jet-label for="address" value="Address:" class="w-32 xl:w-28 mb-0 leading-7" />
                                <div v-if="activeCustomer" class="leading-tight">
                                    <div>{{ activeCustomer.address_1 }}</div>
                                    <div>{{ activeCustomer.address_2 }}</div>
                                    <div>{{ activeCustomer.city }} {{ activeCustomer.province }}, {{ activeCustomer.postal_code }}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="w-1/3">
                        <div class="flex items-start">
                            <jet-label for="notes" value="Shipping Notes:" class="w-32 xl:w-28 mb-0 leading-7" />
                            <div class="flex flex-1">
                                <textarea class="w-full resize-y" v-model="form.notes" style="min-height: 98px;"></textarea>
                            </div>
                        </div>
                    </div>
                </div>

                <order-items-edit class="my-4"
                    :order="order"
                    :form="form"
                    :customers="[activeCustomer]"
                    :products="products"
                    :categories="categories"
                    :is_wholesale="order.is_wholesale"
                    :orderMinimum="orderMinimum"
                    :deliveryFeeBelowMinimum="deliveryFeeBelowMinimum"
                    @updateCanPlaceOrder="updateCanPlaceOrder"
                />
            </div>
        </form>

        <div class="text-center py-24" >
            <p v-if="order.status === -1" class="text-2xl text-red-700 opacity-50">--- Cancelled ---</p>

            <jet-danger-button @click="showCancelModal = true" v-else>
                Cancel Order
            </jet-danger-button>
        </div>

        <unsaved-changes-modal-2
            :formRoute="formRoute"
            :isDirty="form.isDirty"
            :show="showUnsavedChangesModal"
            @close="showUnsavedChangesModal = false"
            @confirm="$inertia.get(route('customer_orders.index'))"
        />

        <jet-confirmation-modal :show="showCancelModal" @close="showCancelModal = false">
            <template #title>
                Cancel Order
            </template>

            <template #content>
                Are you sure you would like to cancel this order?
            </template>

            <template #footer>
                <jet-danger-button @click="cancel" :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                    Yes
                </jet-danger-button>

                <jet-tertiary-button class="ml-2" @click="showCancelModal = false">
                    No
                </jet-tertiary-button>
            </template>
        </jet-confirmation-modal>
    </app-layout>
</template>

<script>
import { useForm } from '@inertiajs/inertia-vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import JetButton from '@/Jetstream/Button';
import JetDangerButton from '@/Jetstream/DangerButton';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import JetInputError from '@/Jetstream/InputError';
import JetLabel from '@/Jetstream/Label';
import OrderItemsEdit from '@/Shared/Orders/OrderItemsEdit';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal';
import UnsavedChangesModal2 from '@/Shared/UnsavedChangesModal2';
import Datepicker from '@vuepic/vue-datepicker';
import { DateTime } from 'luxon';
import Multiselect from '@vueform/multiselect';

import '@vueform/multiselect/themes/default.css';
import '@vuepic/vue-datepicker/dist/main.css';

export default {
    props: ['order', 'products', 'categories', 'holidays', 'orderMinimum', 'deliveryFeeBelowMinimum'],

    setup(props) {
        const form = useForm({
            id: props.order.id,
            customer_id: props.order.customer_id,
            allow_substitutions: props.order.allow_substitutions,
            ship_date: new Date(props.order.ship_date + ' EST'),
            shipping_method: props.order.shipping_method,
            delivery_fee: props.order.delivery_fee,
            applied_credit_amount: props.order.applied_credit_amount,
            notes: props.order.notes,
            order_items: props.order.order_items.map(i => {
                return {
                    id: i.id,
                    order_id: i.order_id,
                    product_id: i.product_id,
                    substitution: i.substitution,
                    title: i.title,
                    description: i.description,
                    product_notes: i.product_notes,
                    size: i.size,
                    tax: i.tax,
                    partial_qty: i.partial_qty,
                    cost: i.cost,
                    price: i.price,
                    quantity: i.quantity,
                    orig_quantity: i.orig_quantity,
                    product: i.product,
                }
            })
        })

        return { form }
    },

    components: {
        AppLayout,
        PageHeader,
        JetButton,
        JetTertiaryButton,
        JetDangerButton,
        JetInputError,
        JetLabel,
        OrderItemsEdit,
        JetConfirmationModal,
        UnsavedChangesModal2,
        Datepicker,
        DateTime,
        Multiselect,
    },

    data() {
        return {
            showCancelModal: false,
            showUnsavedChangesModal: false,
            formRoute: this.route('customer_orders.update', this.order.id),

            shippingOptions: [
                { value: 'delivery', label: 'Delivery' },
                { value: 'pickup', label: 'Pickup' },
            ],

            canPlaceOrder: false,
        }
    },

    computed: {
        minDate () {
            let today = DateTime.now();

            if(this.hasSpecialOrderItems || this.hasDairyItems)
            {
                // If order has special order/dairy items, enforce 2 day window when editing order
                if(today.weekday === 5) today = today.plus({ days: 4 });
                else if(today.weekday === 4) today = today.plus({ days: 3 });
                else today = today.plus({ days: 2 });
            }
            else if(this.form.shipping_method === 'delivery' && today.toLocaleString(DateTime.TIME_24_SIMPLE) >= '04:00')
            {
                today = today.plus({ days: 1 });
            }
            else if (today.hour >= 16) {
                today = today.plus({ days: 1 });
            }

            return today.toJSDate();
        },

        maxDate () {
            let today = new Date();
            return new Date(today.setMonth(today.getMonth() + 3));
        },

        customerDisabledDates () {
            if (!this.activeCustomer) {
                return [];
            }

            let invalidDates = [];
            let customerDays = this.activeCustomer.delivery_days.toLowerCase();
            let nextDate = DateTime.fromJSDate(this.minDate);
            let endDate = DateTime.fromJSDate(this.maxDate);
            let isDelivery = this.form.shipping_method == 'delivery';
            let holiday_adjusted_ship_dates = [];
            let holiday_adjusted_ship_date_def = [];
            let non_adjustable_holidays = [];

            // Get Holiday Adjustments for Schools
            if (this.activeCustomer.type === 'School') {
                let school_holidays = this.holidays;
                let school_only_holidays = ['2025-04-21'];
                school_holidays = school_holidays.concat(school_only_holidays);

                let holidays_desc = school_holidays.sort((a, b) => new Date(b) - new Date(a));

                // Adjust for holidays
                holidays_desc.forEach((holiday) => {
                    let holidate = DateTime.fromISO(holiday);
                    let day_of_week = holidate.setLocale("en").weekdayLong.toLowerCase()
                    let isMonday = day_of_week === 'monday';
                    let tmrw_date = holidate.plus({ days: 1 });

                    // Monday => Tuesday and Tuesday => Wednesday
                    if (isMonday && !holiday_adjusted_ship_date_def.hasOwnProperty(tmrw_date.toFormat('yyyy-LL-dd')))
                    {
                        let tmrw_tmrw_date = tmrw_date.plus({ days: 1 });
                        holiday_adjusted_ship_date_def[tmrw_date.toFormat('yyyy-LL-dd')] = {
                            holiday: false,
                            adj_date: tmrw_tmrw_date.toFormat('yyyy-LL-dd'),
                        };
                    }

                    holiday_adjusted_ship_date_def[holidate.toFormat('yyyy-LL-dd')] = {
                        holiday: true,
                        adj_date: tmrw_date.toFormat('yyyy-LL-dd'),
                    };
                });

                // Adjust for adjusted dates falling on holidays
                for (let [holidate, holidate_data] of Object.entries(holiday_adjusted_ship_date_def))
                {
                    let new_date = holidate_data['adj_date'];
                    while (holiday_adjusted_ship_date_def.hasOwnProperty(new_date) && holiday_adjusted_ship_date_def[new_date]['holiday'])
                    {
                        new_date = holiday_adjusted_ship_date_def[new_date]['adj_date'];
                        holiday_adjusted_ship_date_def[holidate]['adj_date'] = new_date;
                    }
                };
            }

            while (nextDate <= endDate) {

                let isBeforeEarliestShipDay = false;
                if (this.activeCustomer.type === 'School' && nextDate.diff(this.earliestPossibleShipDateForSchool, 'days').days < 0) {
                    isBeforeEarliestShipDay = true;
                }

                let checkDay = nextDate.setLocale("en").weekdayLong.toLowerCase();
                let isSunday = checkDay === 'sunday';
                let deliveryNotAccepted = !customerDays.includes(checkDay);
                let isHoliday = this.holidays.includes(nextDate.toFormat('yyyy-LL-dd'));
                let isAdjustedDueToHoliday = holiday_adjusted_ship_date_def.hasOwnProperty(nextDate.toFormat('yyyy-LL-dd'));
                let isHolidayAdjustedDate = holiday_adjusted_ship_dates.includes(nextDate.toFormat('yyyy-LL-dd'))

                if ((isSunday || isHoliday || (isDelivery && deliveryNotAccepted) || isBeforeEarliestShipDay || isAdjustedDueToHoliday) && !isHolidayAdjustedDate) {
                    invalidDates.push(nextDate.toJSDate());

                    if (customerDays.includes(checkDay) && isAdjustedDueToHoliday && !non_adjustable_holidays.includes(nextDate.toFormat('yyyy-LL-dd')))
                    {
                        holiday_adjusted_ship_dates.push(holiday_adjusted_ship_date_def[nextDate.toFormat('yyyy-LL-dd')]['adj_date']);
                    }
                }

                nextDate = nextDate.plus({ days: 1 });
            }

            return invalidDates;
        },

        dairyCategory() {
            let category = this.categories.find(c => c.id == 96);

            if(category === undefined) return null;

            return category.id;
        },

        hasSpecialOrderItems() {
            return this.form.order_items.filter(i => i.product.special_order === true).length > 0;
        },

        hasDairyItems() {
            if(!this.dairyCategory) return false;

            return this.form.order_items.filter(i => i.product.category_id === this.dairyCategory).length > 0;
        },

        activeCustomer() {
            return this.$page.props.activeCustomer;
        },
    },

    methods: {
        submit () {
            if (!this.form.processing) {
                this.form
                    .transform((data) => ({
                        ...data,
                        ship_date: data.ship_date ? this.formatDate(data.ship_date) : null,
                    }))
                    .submit('put', route('customer_orders.update', this.order.id));
            }
        },

        cancel () {
            if(!this.form.processing) this.form.delete(route('customer_orders.destroy', this.order.id));
        },

        formatDate(date) {
            return DateTime.fromJSDate(date).toFormat('yyyy-LL-dd');
        },

        updateCanPlaceOrder(value) {
            this.canPlaceOrder = value;
        },

        earliestPossibleShipDateForSchool() {
            let orderDate = DateTime.fromISO(this.order.order_date);

            let orderDayOfWeek = orderDate.toFormat('E');

            if (orderDayOfWeek > 3) { // Order day is after Wednesday
                return  orderDate.plus({weeks:2}).startOf('week');
            }
            return orderDate.plus({weeks:1}).startOf('week');
        }
    }
}
</script>

<style>
.dp-custom-input {
    @apply h-7;
}
</style>
